import { formatCurrency } from '@client/lib';
import type { DiffFormat, DiffRow, PolicyDiffGroup } from '../hooks/useEngineDiff';

/** Shared value formatter used by both the diff table and the debug bundle. */
export function formatDiffValue(format: DiffFormat, value: number | undefined): string {
  if (value === undefined) return '—';
  if (format === 'percent') return `${(value * 100).toFixed(1)}%`;
  return formatCurrency(value);
}

function rowLine(row: DiffRow): string {
  const delta = row.differs ? `${(row.relDiff * 100).toFixed(1)}%` : '';
  const flag = row.differs ? ' ⚠️' : '';
  return `| ${row.label}${flag} | ${formatDiffValue(row.format, row.legacy)} | ${formatDiffValue(
    row.format,
    row.next,
  )} | ${delta} |`;
}

function diffTable(rows: DiffRow[]): string {
  return ['| Field | Legacy | New | Δ |', '| --- | ---: | ---: | ---: |', ...rows.map(rowLine)].join(
    '\n',
  );
}

function jsonBlock(obj: unknown): string {
  return ['```json', JSON.stringify(obj ?? null, null, 2), '```'].join('\n');
}

/**
 * Assembles a self-contained, LLM-pasteable debug bundle for one policy's
 * engine diff: the field-by-field legacy-vs-new table (so the reader knows what
 * diverges and by how much) followed by the raw INPUTS that feed both engines.
 * Outputs/ledgers and code are intentionally omitted — the LLM is assumed to
 * have the codebase and can re-derive outputs from these inputs.
 */
export function buildEngineDiffBundle(args: {
  client: Client;
  partner: Client | null;
  policy: PolicyDiffGroup;
}): string {
  const { client, partner, policy } = args;
  const id = policy.fundingSourceId;
  const policyTerms = client.multipleFundingSources?.ltcPolicy?.[id];
  const partnerPolicyTerms = partner?.multipleFundingSources?.ltcPolicy?.[id];
  const isJoint = Boolean(partnerPolicyTerms);

  const sections: string[] = [];

  sections.push(
    `# Engine diff debug bundle — ${policy.label} (${id})`,
    [
      `- Client: \`${client.clientId}\``,
      `- Policy type: \`${policyTerms?.policyType ?? 'unknown'}\``,
      `- Joint: ${isJoint ? 'yes' : 'no'}`,
      `- clientHasStartedLtc: ${client.intakeSurvey?.clientHasStartedLtc ?? 'unknown'}`,
      '- Engines: legacy phase-based coverage vs. month-based claim-projection engine',
    ].join('\n'),
  );

  sections.push('## Diff (legacy vs new)', '### Policy totals', diffTable(policy.scalarRows));
  for (const group of policy.phaseGroups) {
    sections.push(`### Phase — ${group.label}`, diffTable(group.rows));
  }

  sections.push(
    '## Inputs',
    '### Calculation settings',
    jsonBlock(client.clientCalculationSettings),
    '### Demographics',
    jsonBlock(client.clientDemographics),
    '### Care-plan timing (per phase)',
    jsonBlock({
      predictedStartYears: client.clientPhasePredictedStartYears,
      predictedEndYears: client.clientPhasePredictedEndYears,
      yearsTillPhaseEnd: client.clientYearsTillPhaseEnd,
      phaseInflationFactors: client.clientPhaseInflationFactors,
    }),
    '### Care environment & duration selections',
    jsonBlock({
      careEnvironmentSelections: client.careEnvironmentSelections,
      careEnvironmentCosts: client.careEnvironmentCosts,
      carePhaseDurationSelections: client.carePhaseDurationSelections,
    }),
    '### Phase costs (cost-side input)',
    jsonBlock(client.phaseCosts),
    '### Policy terms (funding source)',
    jsonBlock(policyTerms),
    '### Projection inputs (normalized care/coverage input to the new engine)',
    jsonBlock(client.projectionInputs),
  );

  if (isJoint) {
    sections.push(
      '## Partner inputs (joint policy)',
      '### Partner policy terms',
      jsonBlock(partnerPolicyTerms),
      '### Partner projection inputs',
      jsonBlock(partner?.projectionInputs),
    );
  }

  return sections.join('\n\n');
}
